/**
 * Declared match ports: tagged Links (DeclareMatchPorts) expand into per-value
 * JointJS ports. String / other sources are driven by model.MatchPorts[sourceName].
 * Enum sources (ConditionType "enum" + EnumValues) auto-expand from the palette list.
 * Bool sources (ConditionType "bool" / "boolean") auto-expand to true/false.
 * Captions come from TrueLabel / FalseLabel, or Yes / No when those are blank.
 */

const MATCH_PORT_SEP = '__';

/**
 * @param {string} sourceName  Out arg or "Return"
 * @param {string|number} value
 * @param {number} index
 */
export function makeMatchPortId(sourceName, value, index) {
    const v = value == null || value === '' ? `i${index}` : String(value);
    // Keep Joint port ids free of characters that break selectors; encode value safely.
    const safe = encodeURIComponent(v).replace(/%/g, '_');
    return `${sourceName}${MATCH_PORT_SEP}${safe}`;
}

/**
 * @param {string} portId
 * @returns {{ sourceName: string, encodedValue: string } | null}
 */
export function parseMatchPortId(portId) {
    if (!portId || typeof portId !== 'string')
        return null;
    const i = portId.indexOf(MATCH_PORT_SEP);
    if (i <= 0)
        return null;
    return {
        sourceName: portId.slice(0, i),
        encodedValue: portId.slice(i + MATCH_PORT_SEP.length),
    };
}

/**
 * @param {object} link  Palette / tool Link def
 */
export function isDeclareMatchLink(link) {
    return !!(link && (link.DeclareMatchPorts === true || link.DeclareMatchPorts === 'true'));
}

/**
 * DeclareMatchPorts + ConditionType enum + a non-empty EnumValues list.
 * These expand automatically; they are not user-editable MatchPorts sources.
 * @param {object} link
 */
export function isEnumAutoMatchLink(link) {
    if (!isDeclareMatchLink(link))
        return false;
    const type = link.ConditionType == null ? '' : String(link.ConditionType);
    if (type.toLowerCase() !== 'enum')
        return false;
    return Array.isArray(link.EnumValues) && link.EnumValues.length > 0;
}

/**
 * DeclareMatchPorts + ConditionType bool/boolean.
 * These expand automatically to true/false; they are not user-editable MatchPorts sources.
 * @param {object} link
 */
export function isBoolAutoMatchLink(link) {
    if (!isDeclareMatchLink(link))
        return false;
    const type = link.ConditionType == null ? '' : String(link.ConditionType).toLowerCase();
    return type === 'bool' || type === 'boolean';
}

/**
 * Auto-expanded DeclareMatchPorts links (enum or bool); no MatchPorts editor.
 * @param {object} link
 */
export function isAutoMatchLink(link) {
    return isEnumAutoMatchLink(link) || isBoolAutoMatchLink(link);
}

/**
 * User-declared MatchPorts sources only (excludes enum/bool auto links).
 * @param {object} tool
 * @returns {object[]}
 */
export function getDeclareMatchSources(tool) {
    if (!tool?.Links?.length)
        return [];
    return tool.Links.filter(link => isDeclareMatchLink(link) && !isAutoMatchLink(link));
}

/**
 * Build schema fragment for MatchPorts (one array per tagged source).
 * @param {object} tool
 */
export function buildMatchPortsSchema(tool) {
    const sources = getDeclareMatchSources(tool);
    if (!sources.length)
        return null;

    const properties = {};
    for (const link of sources) {
        const name = link.Name;
        properties[name] = {
            type: 'array',
            title: (link.Tooltip || name || 'KEYS').toString().toUpperCase(),
            default: [],
            items: {
                type: 'object',
                properties: {
                    Value: { type: 'string', title: 'Value' },
                    Label: { type: 'string', title: 'Label' },
                },
            },
            formatData: {},
        };
    }
    return {
        type: 'object',
        title: 'Match Ports',
        properties,
    };
}

/**
 * SchemaForm layout items for Match Ports arrays only (Value/Label columns).
 * Used under custom tool forms and appended by buildEssentialIconForm.
 * @param {object} tool
 * @returns {Array}
 */
export function buildMatchPortsFormItems(tool) {
    const sources = getDeclareMatchSources(tool);
    const items = [];
    for (const link of sources) {
        const name = link.Name;
        const title = (link.Tooltip || name || 'KEYS').toString().toUpperCase();
        items.push({
            key: `MatchPorts.${name}`,
            type: 'array',
            title,
            add: 'Add key',
            nodrag: true,
            items: [{
                type: 'section',
                notitle: true,
                ControlData: {
                    columns: 2,
                    gridTemplateColumns: 'minmax(52px, 72px) minmax(0, 1fr)',
                },
                items: [
                    { key: `MatchPorts.${name}[].Value`, notitle: true },
                    { key: `MatchPorts.${name}[].Label`, notitle: true },
                ],
            }],
        });
    }
    return items;
}

/**
 * Essentials overlay SchemaForm layout: default fields + side-by-side MatchPorts rows.
 * @param {object} schema  Root icon schema (API / Arguments / Output / MatchPorts)
 * @param {object} tool
 * @returns {Array}
 */
export function buildEssentialIconForm(schema, tool) {
    const matchItems = buildMatchPortsFormItems(tool);
    if (!matchItems.length)
        return ['*'];

    const props = schema?.properties || {};
    const form = ['API', 'Arguments', 'Output'].filter(k => k in props);
    return form.concat(matchItems);
}

/**
 * @param {object} entry  { Value, Label }
 * @param {object} baseLink  Original DeclareMatchPorts link
 * @param {number} index
 */
export function matchEntryToPortLink(entry, baseLink, index) {
    const value = entry?.Value != null ? String(entry.Value) : '';
    const label = entry?.Label != null ? String(entry.Label) : '';
    const display = label
        ? `${baseLink.Name} ${value} · ${label}`
        : `${baseLink.Name} ${value}`.trim();
    return {
        ...baseLink,
        // Synthetic port id (Joint); persisted link.Name stays baseLink.Name
        PortId: makeMatchPortId(baseLink.Name, value, index),
        Name: baseLink.Name,
        Tooltip: display,
        EnableCondition: false,
        DeclareMatch: true,
        DeclareMatchPorts: true,
        MatchValue: value,
        MatchLabel: label,
        MatchIndex: index,
    };
}

/**
 * Caption for a bool match port. Blank or missing fields use the fallback.
 * @param {object} link
 * @param {string} field  TrueLabel or FalseLabel
 * @param {string} fallback
 */
function boolPortLabel(link, field, fallback) {
    const raw = link?.[field];
    const text = raw == null ? '' : String(raw).trim();
    return text || fallback;
}

/**
 * Effective port list: static Links plus expanded DeclareMatchPorts sources
 * (enum-auto from EnumValues; bool-auto true/false; others from model.MatchPorts).
 * @param {object} tool
 * @param {object} model  { MatchPorts?: { [source]: [{Value,Label}] } }
 * @returns {object[]} port link defs (use PortId || Name as Joint port id)
 */
export function getEffectiveLinks(tool, model) {
    const links = tool?.Links;
    if (!Array.isArray(links) || !links.length)
        return [];

    const matchPorts = model?.MatchPorts || {};
    const result = [];

    for (const link of links) {
        if (!isDeclareMatchLink(link)) {
            result.push(link);
            continue;
        }
        if (isEnumAutoMatchLink(link)) {
            for (let i = 0; i < link.EnumValues.length; i++) {
                const raw = link.EnumValues[i];
                if (raw == null || raw === '')
                    continue;
                const value = String(raw);
                const port = matchEntryToPortLink({ Value: value, Label: '' }, link, i);
                port.Tooltip = value;
                result.push(port);
            }
            continue;
        }
        if (isBoolAutoMatchLink(link)) {
            const boolPorts = [
                { Value: 'true', Label: boolPortLabel(link, 'TrueLabel', 'Yes') },
                { Value: 'false', Label: boolPortLabel(link, 'FalseLabel', 'No') },
            ];
            for (let i = 0; i < boolPorts.length; i++) {
                const entry = boolPorts[i];
                const port = matchEntryToPortLink({ Value: entry.Value, Label: '' }, link, i);
                port.Tooltip = entry.Label;
                result.push(port);
            }
            continue;
        }
        const entries = Array.isArray(matchPorts[link.Name]) ? matchPorts[link.Name] : [];
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            if (!entry || entry.Value == null || entry.Value === '')
                continue;
            result.push(matchEntryToPortLink(entry, link, i));
        }
    }
    return result;
}

/**
 * Joint port id for a link def from getEffectiveLinks.
 * @param {object} p
 */
export function getPortId(p) {
    return p.PortId || p.Name;
}

/**
 * Resolve stored flow link { Name, Condition } to Joint port id using effective links.
 * @param {object} storedLink
 * @param {object[]} effectiveLinks
 */
export function resolveStoredLinkPortId(storedLink, effectiveLinks) {
    if (!storedLink)
        return null;
    const matchPort = effectiveLinks.find(p =>
        p.DeclareMatch &&
        p.Name === storedLink.Name &&
        String(p.MatchValue) === String(storedLink.Condition ?? ''));
    if (matchPort)
        return getPortId(matchPort);
    const byName = effectiveLinks.find(p => getPortId(p) === storedLink.Name || p.Name === storedLink.Name);
    if (byName && !byName.DeclareMatch)
        return getPortId(byName);
    return storedLink.Name;
}

/**
 * Format port label text for a match or static link.
 * @param {object} p
 * @param {function} formatPortLabel
 */
export function portLabelText(p, formatPortLabel) {
    const raw = p.Tooltip || p.Name || '';
    return typeof formatPortLabel === 'function' ? formatPortLabel(raw) : raw;
}
